import React, { FC, ReactElement, useCallback } from 'react';
import { Button, RadioGroup, Radio, MenuItem } from '@cherre-frontend/ui';
import * as C from '../../styles';

import {
  useCustomSource,
  useSaveCustomSource,
  useSaveCustomSourceFormSecondStep,
} from 'src/products/connector/pages/add-connector/dataState';
import {
  getAvailability,
  getFrequencyOptions,
  setAvailability,
} from '../../SecondStep.utils';
import { SourceCustomFormSecondStepContainer } from './styles';
import { useConnectorUIState } from 'src/products/connector/pages/add-connector/uiState/contexts';

const SourceCustomFormSecondStep: FC = (): ReactElement => {
  const [data, setData] = useCustomSource();
  const [{ chosenSource }] = useConnectorUIState();
  const [setSave] = useSaveCustomSource();
  const [canSave, previousStep, closeStep] =
    useSaveCustomSourceFormSecondStep();

  const handleSave = useCallback(() => {
    if (!chosenSource) {
      return;
    }

    const {
      name: preconfiguredSourceName,
      provider,
      stableVersion,
      displayName,
      configuration,
    } = chosenSource;

    const addSource = {
      preconfiguredSourceName,
      provider,
      stableVersion,
      displayName,
      configuration,
    };

    const localData = {
      ...data,
      ...addSource,
    };

    setSave(localData);
  }, [chosenSource, data, setSave, setData]);

  const handleAvailabiliyOnChanges = useCallback((newValue) => {
    setData({ availability: setAvailability(newValue) });
  }, []);

  const handleFrequencyRadioOnChanges = useCallback((event) => {
    setData({
      updateFrequency: { type: event.target.value as any, day: 1 },
    });
  }, []);

  const handleFrequencySelectOnChanges = useCallback(
    (event) => {
      if (!data.updateFrequency) {
        return;
      }

      setData({
        updateFrequency: {
          type: data.updateFrequency.type,
          day: event.target.value as any,
        },
      });
    },
    [data]
  );

  const handleCancel = useCallback(() => {
    closeStep();
  }, [closeStep]);

  return (
    <SourceCustomFormSecondStepContainer>
      <C.FieldBox>
        <C.FieldTitle>Source Availability:</C.FieldTitle>
        <C.FieldDescription>
          Note: Choose the time of day that the source data will be available to
          ingest.
        </C.FieldDescription>
        <C.FieldTimePicker
          value={getAvailability(data.availability)}
          onChange={handleAvailabiliyOnChanges}
          slotProps={{
            textField: {
              inputProps: { 'data-testid': 'sftp-source-availability' },
            },
          }}
        />
      </C.FieldBox>
      <C.FieldBox>
        <C.FieldTitle>Update Frequency:</C.FieldTitle>
        <RadioGroup
          row
          value={data.updateFrequency?.type || null}
          onChange={handleFrequencyRadioOnChanges}
        >
          <Radio label='Daily' value='daily' />
          <Radio label='Weekdays Only' value='weekdays' />
          <Radio label='Weekly' value='weekly' />
          <Radio label='Monthly' value='monthly' />
        </RadioGroup>
        <C.FieldSelect
          value={(data.updateFrequency as any)?.day ?? ''}
          disabled={getFrequencyOptions(data.updateFrequency).length === 0}
          onChange={handleFrequencySelectOnChanges}
          inputProps={{ 'data-testid': 'sftp-source-frequency' }}
        >
          {getFrequencyOptions(data.updateFrequency).map(([label, value]) => (
            <MenuItem key={label} value={value}>
              {label}
            </MenuItem>
          ))}
        </C.FieldSelect>
      </C.FieldBox>
      <C.Actions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={previousStep} variant='contained'>
          Previous
        </Button>
        <Button onClick={handleSave} disabled={!canSave} variant='contained'>
          Save
        </Button>
      </C.Actions>
    </SourceCustomFormSecondStepContainer>
  );
};

export default SourceCustomFormSecondStep;
